import { getBrandName } from '@/lib/brandName';

// Staff-facing language for Billing Readiness / safeguard codes (never show raw codes alone on normal screens).
export const READINESS_CODES = {
  PENDING_TIMESHEET: 'Worker timesheet not yet approved for this service.',
  PENDING_VERIFICATION: 'Manual service verification is still in progress.',
  NON_BILLABLE: 'This service was recorded as non-billable (e.g. late cancellation rule).',
  NO_ACTIVE_AGREEMENT: 'No active Service Agreement covers this service date.',
  NO_AGREEMENT_LINE: 'The Service Agreement has no matching service line for this service.',
  NO_FUNDING_SOURCE: 'No funding method (Self / Plan / Agency) applies on this service date.',
  NO_PRICING_VERSION: 'No published NDIS pricing version covers this service date.',
  NO_SERVICE_ITEM: 'No valid NDIS Support Item could be resolved for this service.',
  AMBIGUOUS_SERVICE_ITEM: 'More than one NDIS Support Item matches — the service line needs a single item.',
  ITEM_EXPIRED: 'The NDIS Support Item is not valid on this service date.',
  NO_VALID_RATE: 'No valid rate could be resolved from the agreement or price table.',
  RATE_EXCEEDS_CAP: 'Agreed rate is above the NDIS price cap (allowed for self-managed funding, blocked otherwise).',
  QUANTITY_UNAPPROVED: 'Approved service quantity/time is missing.',
  get MY_PROVIDER_REQUIRED() { return `My Provider relationship required — the participant has not confirmed ${getBrandName()} as My Provider for this plan.`; },
  MY_PROVIDER_NOT_CONFIRMED: 'My Provider relationship is recorded but not yet confirmed.',
  get PROVIDER_NOT_REGISTERED() { return `${getBrandName()} is not registered for the NDIS registration group of this support item.`; },
  NO_PLAN_MANAGER_DETAILS: 'Plan Manager details are missing on the agreement/funding record.',
  POSSIBLE_DUPLICATE: 'A billing record already appears to cover this participant, item and period.',
  get FUNDING_EXCEEDED() { return `This service would exceed the participant's ${getBrandName()} tracked funding.`; },
  TWO_TO_ONE_NOT_ENABLED: '2:1 support billing is not enabled (owner decision P41) — the segment is not priced.',
  DUAL_WORKER_EVIDENCE_MISSING: 'Both workers\' approved evidence is required for a 2:1 segment.',
  READY_FOR_BILLING: 'Ready for billing.',
  READINESS_BLOCKED: 'Billing Readiness blocked this financial action.',
  // P34 / P55 owner-approved wording (raw code stays visible for diagnostics where shown)
  AGREEMENT_LEGAL_TERMS_NOT_APPROVED: 'Agreement signing is currently unavailable because the approved Service Agreement terms have not yet been configured. Please contact an administrator.',
  SHARED_PARTICIPANT_VISIBILITY_RESTRICTED: 'You do not have permission to view this participant. Shared-support participant access is currently restricted.',
  PARTICIPANT_NOT_ASSIGNED: 'You do not have permission to view this participant. Shared-support participant access is currently restricted.',
  ASSIGNMENT_BLOCKED: 'This worker cannot be assigned — a mandatory requirement (e.g. valid NDIS Worker Screening) is missing.',
  WORKER_SCREENING_INVALID: 'NDIS Worker Screening is missing, expired or unverified.',
  MANDATORY_QUALIFICATION_MISSING: 'A mandatory qualification for this service is missing or expired.',
  PARTICIPANT_COMPETENCY_MISSING: 'A competency required by this participant is missing.',
  ORG_CONTEXT_REQUIRED: 'Your account is not bound to an organisation; organisation-owned records cannot be created.',
  PARTICIPANT_NOT_ON_SHIFT: 'That participant is not linked to this shift.',
  WINDOW_OUTSIDE_SHIFT: 'Each participant window must sit inside the worker\'s shift time.',
  SCREENING_EVIDENCE_REQUIRED: 'A verified screening needs the clearance reference number and expiry date.',
  HISTORY_IMMUTABLE: 'These records are end-dated, not deleted — record an end date instead.',
  INVALID_TRIAGE_TRANSITION: 'That triage step is not available from the current status.',
  WORKER_ALREADY_RESPONDED: 'The worker has already responded to this feedback.',
  RESPONSE_NOT_REQUESTED: 'A response has not been requested for this feedback.',
  DUPLICATE_BILLING_RECORD: 'A billing record already exists for this service — duplicates are refused.',
  INVALID_TRANSITION: 'That status change is not allowed from the current status.',
};

export const friendlyCode = (code) => READINESS_CODES[code] || (code ? String(code).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : '');

// Extract a structured error code from an API error response.
export const apiErrorCode = (err) => {
  const d = err?.response?.data;
  return d?.error?.code || d?.detail?.code || d?.code || null;
};

// Friendly message first, raw code kept in brackets for diagnostics.
export const friendlyError = (err, fallback = 'Action failed') => {
  const code = apiErrorCode(err);
  const raw = err?.response?.data?.error?.message || err?.response?.data?.detail?.message || (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : null);
  if (code && READINESS_CODES[code]) return `${READINESS_CODES[code]} (${code})`;
  return raw || err?.message || fallback;   // Day 22 convention kept: backend message, humanised code only when no message
};

/** UA-6-F21: same as friendlyError, but a JSON error body returned to a `responseType: 'blob'` request is parsed first. */
export const friendlyErrorAsync = async (err, fallback = 'Action failed') => {
  const d = err?.response?.data;
  if (d && typeof Blob !== 'undefined' && d instanceof Blob) {
    try { err.response.data = JSON.parse(await blobText(d)); } catch { /* not JSON — keep the blob */ }
  }
  return friendlyError(err, fallback);
};
const blobText = (b) => (typeof b.text === 'function' ? b.text() : new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = reject; r.readAsText(b); }));

// Batch 3 O4: one reader for the roster block payload (409 ASSIGNMENT_BLOCKED from assign / add-staff / bulk / reschedule /
// create / swap / accept). Returns { title, reasons[] } or null when the error is not an assignment block.
export const assignmentBlock = (err) => {
  const d = err?.response?.data;
  const body = d?.error || d?.detail;
  if (!body || body.code !== 'ASSIGNMENT_BLOCKED') return null;
  const det = body.details || {};
  const blocks = (det.blocks || []).filter((b) => !b.sensitive).map((b) => b.message);
  const issues = (det.blockingIssues || []).map((b) => b.message);
  const reasons = Array.from(new Set([...blocks, ...issues].filter(Boolean)));
  const overlap = (det.block_codes || []).includes('OVERLAPPING_SHIFT') || (det.blockingIssues || []).some((b) => b.conflictType === 'OVERLAPPING_SHIFT');
  return { title: overlap ? 'Worker already has an overlapping shift' : (body.message || READINESS_CODES.ASSIGNMENT_BLOCKED), reasons, overlap };
};

export const readinessStatus = (row) => {
  if (!row) return 'PENDING EVIDENCE';
  const codes = row.block_codes || row.codes || [];
  if (row.ready) return (row.warning_codes || row.warnings || []).length ? 'WARNING' : 'READY';
  if (codes.includes('NON_BILLABLE')) return 'NON-BILLABLE';
  if (codes.includes('PENDING_TIMESHEET') || codes.includes('PENDING_VERIFICATION')) return 'PENDING EVIDENCE';
  return 'BLOCKED';
};

export const STATUS_STYLE = {
  READY: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  WARNING: 'bg-amber-100 text-amber-800 border-amber-200',
  BLOCKED: 'bg-red-100 text-red-800 border-red-200',
  'NON-BILLABLE': 'bg-slate-100 text-slate-700 border-slate-200',
  'PENDING EVIDENCE': 'bg-sky-100 text-sky-800 border-sky-200',
};

export const AGREEMENT_ACTION_LABELS = {
  submit_review: 'Submit for internal review',
  request_changes: 'Request changes',
  return_to_draft: 'Return to draft',
  approve_for_signature: 'Approve for signature',
  send: 'Send for signature',
  decline: 'Record decline',
  lapse: 'Mark signature lapsed',
  sign: 'Record signature',
  activate: 'Activate',
  suspend: 'Suspend',
  resume: 'Resume',
  terminate: 'Terminate',
  cancel_draft: 'Cancel (unsigned)',
  return_to_draft_for_regeneration: 'Return to draft for regeneration (new version)',
};

// UA-4-F9: render an ASSIGNMENT_BLOCKED toast once — identical title+reasons within 2 s are suppressed (handler + wrapper both reporting).
let _lastBlockToast = { key: '', at: 0 };
export const toastAssignmentBlock = (toast, blk, opts = {}) => {
  const key = `${blk.title}|${(blk.reasons || []).join('|')}`;
  const now = Date.now();
  if (_lastBlockToast.key === key && now - _lastBlockToast.at < 2000) return false;
  _lastBlockToast = { key, at: now };
  toast.error(blk.title, { description: (blk.reasons || []).join(' · ') || undefined, ...opts });
  return true;
};
